import type { UserOwnView } from "@shared/schemas/user-views.schemas";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import { useGatedAction } from "../gated-action";
import type { AdminActions } from "./use-admin-actions";
import { useAdminActions } from "./use-admin-actions";
import type { EventActions, EventActionsInput } from "./use-event-actions";
import { useEventActions } from "./use-event-actions";
import type { EventManagerActions } from "./use-event-manager-actions";
import { useEventManagerActions } from "./use-event-manager-actions";

type AccountHooks = FrontendDAL["account"];
type EventHooks = FrontendDAL["events"];
type FeatureConfigHooks = FrontendDAL["featureConfig"];

function useGetUserEventsAsParticipant(): ReturnType<
  AccountHooks["useGetUserEventsAsParticipant"]
> {
  return dal().account.useGetUserEventsAsParticipant();
}

function useGetMyEvents(
  options?: Parameters<EventHooks["useGetMyEvents"]>[0],
): ReturnType<EventHooks["useGetMyEvents"]> {
  return dal().events.useGetMyEvents(options);
}

function useGetIsolationStatus(
  options?: Parameters<AccountHooks["useGetIsolationStatus"]>[0],
): ReturnType<AccountHooks["useGetIsolationStatus"]> {
  return dal().account.useGetIsolationStatus(options);
}

function useGetFeatureConfigs(
  input: Parameters<FeatureConfigHooks["useGetFeatureConfigsUserView"]>[0],
): ReturnType<FeatureConfigHooks["useGetFeatureConfigsUserView"]> {
  return dal().featureConfig.useGetFeatureConfigsUserView(input);
}

export interface UserActionsExtra {
  readonly isAuthenticated: boolean;
  readonly logout: () => Promise<void>;
  readonly refresh: () => Promise<void>;
  readonly useForEventManager: () => EventManagerActions;
  readonly useForEvent: (input: EventActionsInput) => EventActions;
  readonly useForAdmin: () => AdminActions;
  readonly sendVerificationEmail: ReturnType<
    AccountHooks["useSendVerificationEmail"]
  >;
  readonly validateVerificationCode: ReturnType<
    AccountHooks["useValidateVerificationCode"]
  >;
  readonly useGetUserEventsAsParticipant: () => ReturnType<
    AccountHooks["useGetUserEventsAsParticipant"]
  >;
  readonly useGetMyEvents: (
    options?: Parameters<EventHooks["useGetMyEvents"]>[0],
  ) => ReturnType<EventHooks["useGetMyEvents"]>;
  readonly useGetIsolationStatus: (
    options?: Parameters<AccountHooks["useGetIsolationStatus"]>[0],
  ) => ReturnType<AccountHooks["useGetIsolationStatus"]>;
  readonly useGetFeatureConfigs: (
    input: Parameters<FeatureConfigHooks["useGetFeatureConfigsUserView"]>[0],
  ) => ReturnType<FeatureConfigHooks["useGetFeatureConfigsUserView"]>;
}

export interface UserActions extends UserActionsExtra {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly user: UserOwnView | undefined;
}

/**
 * User actions — the authenticated root of the actions tree (mirrors backend
 * `UserActions`). Gated on authentication: `subject` (surfaced as `user`) is the
 * current user, or `null` when unauthenticated; `isAuthenticated` is `subject !==
 * null` and is what downstream guards read. Always non-null so the nested
 * `useForEvent` / `useForEventManager` / `useForAdmin` accessors are callable
 * unconditionally (Rules of Hooks), mirroring backend `userActions.forEvent()` /
 * `.forEventManager()` / `.forAdmin()`. It has no role-gated mutation bundle: its
 * operations (logout/refresh/verification)
 * and account query hooks are available to any authenticated caller, so they are
 * flat members. Downstream role gates read `user.$roles`.
 */
export function useUserActions(): UserActions {
  const {
    isLoading,
    error,
    subject,
    bundle: _bundle,
    ...extra
  } = useGatedAction({
    useDeps: () => ({
      currentUser: dal().auth.useCurrentUser(),
      logoutMutation: dal().auth.useLogout(),
      refresh: dal().auth.useRefreshCurrentUser(),
      sendVerificationEmail: dal().account.useSendVerificationEmail(),
      validateVerificationCode: dal().account.useValidateVerificationCode(),
    }),
    isLoading: (d) => d.currentUser.isLoading,
    error: (d) =>
      d.currentUser.error instanceof Error ? d.currentUser.error : null,
    authorize: (d) => d.currentUser.data ?? null,
    extra: (d, resolved): UserActionsExtra => ({
      isAuthenticated: resolved.isAuthorized,
      logout: async (): Promise<void> => {
        await d.logoutMutation.mutateAsync();
      },
      refresh: d.refresh,
      useForEventManager: useEventManagerActions,
      useForEvent: useEventActions,
      useForAdmin: useAdminActions,
      sendVerificationEmail: d.sendVerificationEmail,
      validateVerificationCode: d.validateVerificationCode,
      useGetUserEventsAsParticipant,
      useGetMyEvents,
      useGetIsolationStatus,
      useGetFeatureConfigs,
    }),
  });

  return { isLoading, error, user: subject ?? undefined, ...extra };
}
